import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Receipt, CreditCard, ShoppingCart, Wallet, Loader2, Plus, Check, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  draft: "#94A3B8", sent: "#3B82F6", partial: "#F59E0B", paid: "#22C55E", overdue: "#EF4444", void: "#64748B",
  pending: "#F59E0B", completed: "#22C55E", failed: "#EF4444",
  requested: "#F59E0B", approved: "#22C55E", rejected: "#EF4444", fulfilled: "#06B6D4",
};

const fmt = (n) => n == null ? "GH₵ 0" : n >= 1_000_000 ? `GH₵ ${(n / 1_000_000).toFixed(2)}M` : n >= 1000 ? `GH₵ ${(n / 1000).toFixed(1)}K` : `GH₵ ${n.toLocaleString()}`;

export default function FinOps() {
  const [tab, setTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [procurement, setProcurement] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingInv, setCreatingInv] = useState(false);
  const [creatingProc, setCreatingProc] = useState(false);
  const [creatingPay, setCreatingPay] = useState(false);

  const blankInv = { client_name: "", client_email: "", amount: 0, description: "", currency: "GHS" };
  const blankProc = { vendor_name: "", description: "", amount: 0, category: "operational", currency: "GHS" };
  const blankPay = { amount: 0, method: "bank_transfer", invoice_id: "", received_from: "", reference: "" };
  const [newInv, setNewInv] = useState(blankInv);
  const [newProc, setNewProc] = useState(blankProc);
  const [newPay, setNewPay] = useState(blankPay);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/finops/dashboard"),
      api.get("/finops/invoices"),
      api.get("/finops/payments"),
      api.get("/finops/procurement"),
    ]).then(([d, i, p, pr]) => {
      setDashboard(d.data);
      setInvoices(i.data);
      setPayments(p.data);
      setProcurement(pr.data);
    }).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const saveInvoice = async () => {
    if (!newInv.client_name || !newInv.amount) return;
    try {
      await api.post("/finops/invoices", newInv);
      setNewInv(blankInv); setCreatingInv(false); load(); toast.success("Invoice created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendInvoice = async (id) => {
    try { await api.put(`/finops/invoices/${id}`, { status: "sent" }); load(); }
    catch { toast.error("Failed"); }
  };

  const savePayment = async () => {
    if (!newPay.amount) return;
    try {
      await api.post("/finops/payments", newPay);
      setNewPay(blankPay); setCreatingPay(false); load(); toast.success("Payment recorded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveProc = async () => {
    if (!newProc.description || !newProc.amount) return;
    try {
      await api.post("/finops/procurement", newProc);
      setNewProc(blankProc); setCreatingProc(false); load(); toast.success("Procurement request submitted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const decide = async (id, decision) => {
    try { await api.post(`/finops/procurement/${id}/decision`, { decision }); load(); }
    catch { toast.error("Failed"); }
  };

  if (loading || !dashboard) {
    return <div className="p-10 text-slate-400 font-mono text-xs flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> LOADING FINOPS…</div>;
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="finops-page">
      <div>
        <div className="label-eyebrow mb-1">// Financial Operations</div>
        <h1 className="font-display text-3xl text-white tracking-tight">FinOps</h1>
        <p className="text-slate-400 text-sm mt-1 max-w-2xl">
          Invoicing, payments, procurement approvals and budgets — institutional cash flow in one place. GHS-native.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="finops-kpis">
        <Stat label="AR Open" value={fmt(dashboard.ar_open_ghs)} eyebrow="01 // RECEIVABLES" />
        <Stat label="AR Overdue" value={fmt(dashboard.ar_overdue_ghs)} eyebrow="02 // OVERDUE" color={dashboard.ar_overdue_ghs > 0 ? "#EF4444" : undefined} />
        <Stat label="Cash 30d" value={fmt(dashboard.cash_received_30d_ghs)} eyebrow="03 // CASH IN" color="#22C55E" />
        <Stat label="Pending PR" value={`${dashboard.pending_procurement_count} · ${fmt(dashboard.pending_procurement_ghs)}`} eyebrow="04 // PROCUREMENT" />
      </div>

      {/* Tab nav */}
      <div className="flex flex-wrap gap-2 border-b border-white/5 pb-2" data-testid="finops-tabs">
        {["dashboard", "invoices", "payments", "procurement"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`tab-${t}`}
            className={`px-4 py-2 text-[11px] font-mono tracking-widest transition-all ${tab === t ? "bg-[#D4AF37]/10 text-white border-b-2 border-[#D4AF37]" : "text-slate-400 hover:text-white"}`}
          >
            {t.toUpperCase()}
          </button>
        ))}
      </div>

      {tab === "dashboard" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="finops-dashboard">
          <div className="nexus-glass-panel p-5">
            <div className="label-eyebrow mb-3">// Invoice Status</div>
            <div className="space-y-2">
              {Object.entries(dashboard.invoice_counts || {}).length === 0 && <div className="text-[12px] text-slate-500">No invoices yet</div>}
              {Object.entries(dashboard.invoice_counts || {}).map(([s, c]) => (
                <div key={s} className="flex items-center justify-between py-1.5 border-b border-white/5">
                  <span className="font-mono text-[10px] tracking-widest" style={{ color: STATUS_COLOR[s] || "#94A3B8" }}>{s.toUpperCase()}</span>
                  <span className="font-display text-base text-white">{c}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="nexus-glass-panel p-5">
            <div className="label-eyebrow mb-3">// Activity</div>
            <div className="space-y-2 text-sm">
              <Row k="Invoices issued" v={dashboard.total_invoices} />
              <Row k="Payments completed" v={dashboard.total_payments} />
              <Row k="Cash received (90d)" v={fmt(dashboard.cash_received_90d_ghs)} />
              <Row k="Budget total" v={fmt(dashboard.budget_total_ghs)} />
              <Row k="Budget burn" v={`${dashboard.budget_burn_pct}%`} />
            </div>
          </div>
        </div>
      )}

      {tab === "invoices" && (
        <div className="space-y-4" data-testid="invoices-tab">
          <div className="flex justify-between items-center">
            <h3 className="font-display text-lg text-white">Invoices</h3>
            <button onClick={() => setCreatingInv(!creatingInv)} data-testid="new-invoice-btn" className="text-[11px] font-mono tracking-widest px-3 py-1.5 flex items-center gap-1.5 border border-white/15 hover:border-white/30">
              <Plus className="w-3 h-3" /> NEW INVOICE
            </button>
          </div>
          {creatingInv && (
            <div className="nexus-glass-panel p-5 space-y-3" data-testid="invoice-form">
              <input value={newInv.client_name} onChange={(e) => setNewInv({ ...newInv, client_name: e.target.value })} placeholder="Client name" data-testid="inv-client" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
              <div className="grid grid-cols-2 gap-3">
                <input value={newInv.client_email} onChange={(e) => setNewInv({ ...newInv, client_email: e.target.value })} placeholder="Client email" className="bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
                <input type="number" value={newInv.amount} onChange={(e) => setNewInv({ ...newInv, amount: parseFloat(e.target.value) || 0 })} placeholder="Amount (GHS)" data-testid="inv-amount" className="bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
              </div>
              <textarea value={newInv.description} onChange={(e) => setNewInv({ ...newInv, description: e.target.value })} rows={2} placeholder="Description" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm p-2.5 resize-none focus:border-[#D4AF37]/60 focus:outline-none" />
              <button onClick={saveInvoice} data-testid="save-invoice" className="text-[11px] font-mono tracking-widest px-3 py-2" style={{ background: "var(--gold-500)", color: "#050B14" }}>CREATE INVOICE</button>
            </div>
          )}
          {invoices.length === 0 ? <div className="text-center py-10 text-slate-500 text-sm">No invoices yet.</div> : (
            <div className="space-y-2">
              {invoices.map((inv) => (
                <div key={inv.id} className="flex items-center gap-3 p-3 nexus-card" data-testid={`invoice-${inv.id}`}>
                  <Receipt className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{inv.invoice_no} · {inv.client_name}</div>
                    <div className="text-[11px] text-slate-500">{inv.description?.slice(0, 80)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm text-white">{fmt(inv.amount)}</div>
                    {inv.amount_paid > 0 && <div className="text-[11px] text-emerald-400">paid {fmt(inv.amount_paid)}</div>}
                  </div>
                  <span className="font-mono text-[9px] tracking-widest" style={{ color: STATUS_COLOR[inv.status] }}>{inv.status?.toUpperCase()}</span>
                  {inv.status === "draft" && (
                    <button onClick={() => sendInvoice(inv.id)} data-testid={`send-${inv.id}`} className="text-[10px] font-mono tracking-widest px-2 py-1 border border-white/15 hover:border-[#D4AF37]/40 text-slate-300">SEND</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "payments" && (
        <div className="space-y-4" data-testid="payments-tab">
          <div className="flex justify-between items-center">
            <h3 className="font-display text-lg text-white">Payments</h3>
            <button onClick={() => setCreatingPay(!creatingPay)} data-testid="new-payment-btn" className="text-[11px] font-mono tracking-widest px-3 py-1.5 flex items-center gap-1.5 border border-white/15 hover:border-white/30">
              <Plus className="w-3 h-3" /> RECORD PAYMENT
            </button>
          </div>
          {creatingPay && (
            <div className="nexus-glass-panel p-5 space-y-3" data-testid="payment-form">
              <select value={newPay.invoice_id} onChange={(e) => setNewPay({ ...newPay, invoice_id: e.target.value })} className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
                <option value="">— link to invoice (optional) —</option>
                {invoices.filter((i) => i.status !== "paid").map((i) => <option key={i.id} value={i.id}>{i.invoice_no} · {i.client_name} · {fmt(i.amount)}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input type="number" value={newPay.amount} onChange={(e) => setNewPay({ ...newPay, amount: parseFloat(e.target.value) || 0 })} placeholder="Amount (GHS)" data-testid="pay-amount" className="bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
                <select value={newPay.method} onChange={(e) => setNewPay({ ...newPay, method: e.target.value })} className="bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
                  <option value="bank_transfer">Bank transfer</option><option value="mobile_money">Mobile money</option><option value="cheque">Cheque</option><option value="cash">Cash</option><option value="paystack">Paystack</option>
                </select>
              </div>
              <input value={newPay.received_from} onChange={(e) => setNewPay({ ...newPay, received_from: e.target.value })} placeholder="Received from" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
              <input value={newPay.reference} onChange={(e) => setNewPay({ ...newPay, reference: e.target.value })} placeholder="Reference (txn id, cheque no.)" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
              <button onClick={savePayment} data-testid="save-payment" className="text-[11px] font-mono tracking-widest px-3 py-2" style={{ background: "var(--gold-500)", color: "#050B14" }}>RECORD PAYMENT</button>
            </div>
          )}
          {payments.length === 0 ? <div className="text-center py-10 text-slate-500 text-sm">No payments recorded.</div> : (
            <div className="space-y-2">
              {payments.map((p) => (
                <div key={p.id} className="flex items-center gap-3 p-3 nexus-card" data-testid={`payment-${p.id}`}>
                  <CreditCard className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{p.received_from || "—"}</div>
                    <div className="text-[11px] text-slate-500">{p.method} · {p.reference || "no ref"}</div>
                  </div>
                  <div className="font-mono text-sm text-white">{fmt(p.amount)}</div>
                  <span className="font-mono text-[9px] tracking-widest" style={{ color: STATUS_COLOR[p.status] }}>{p.status?.toUpperCase()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "procurement" && (
        <div className="space-y-4" data-testid="procurement-tab">
          <div className="flex justify-between items-center">
            <h3 className="font-display text-lg text-white">Procurement Requests</h3>
            <button onClick={() => setCreatingProc(!creatingProc)} data-testid="new-procurement-btn" className="text-[11px] font-mono tracking-widest px-3 py-1.5 flex items-center gap-1.5 border border-white/15 hover:border-white/30">
              <Plus className="w-3 h-3" /> NEW REQUEST
            </button>
          </div>
          {creatingProc && (
            <div className="nexus-glass-panel p-5 space-y-3" data-testid="procurement-form">
              <input value={newProc.vendor_name} onChange={(e) => setNewProc({ ...newProc, vendor_name: e.target.value })} placeholder="Vendor" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
              <textarea value={newProc.description} onChange={(e) => setNewProc({ ...newProc, description: e.target.value })} rows={2} placeholder="What is being procured" data-testid="proc-desc" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm p-2.5 resize-none focus:border-[#D4AF37]/60 focus:outline-none" />
              <div className="grid grid-cols-2 gap-3">
                <input type="number" value={newProc.amount} onChange={(e) => setNewProc({ ...newProc, amount: parseFloat(e.target.value) || 0 })} placeholder="Amount (GHS)" data-testid="proc-amount" className="bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
                <select value={newProc.category} onChange={(e) => setNewProc({ ...newProc, category: e.target.value })} className="bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
                  <option value="operational">Operational</option><option value="capital">Capital</option><option value="professional_services">Professional services</option><option value="technology">Technology</option><option value="other">Other</option>
                </select>
              </div>
              <button onClick={saveProc} data-testid="save-procurement" className="text-[11px] font-mono tracking-widest px-3 py-2" style={{ background: "var(--gold-500)", color: "#050B14" }}>SUBMIT REQUEST</button>
            </div>
          )}
          {procurement.length === 0 ? <div className="text-center py-10 text-slate-500 text-sm">No procurement requests.</div> : (
            <div className="space-y-2">
              {procurement.map((p) => (
                <div key={p.id} className="flex items-center gap-3 p-3 nexus-card" data-testid={`proc-${p.id}`}>
                  <ShoppingCart className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{p.vendor_name || "—"}</div>
                    <div className="text-[11px] text-slate-500 truncate">{p.description}</div>
                  </div>
                  <div className="font-mono text-sm text-white">{fmt(p.amount)}</div>
                  <span className="font-mono text-[9px] tracking-widest" style={{ color: STATUS_COLOR[p.status] }}>{p.status?.toUpperCase()}</span>
                  {p.status === "requested" && (
                    <div className="flex gap-1">
                      <button onClick={() => decide(p.id, "approved")} data-testid={`approve-${p.id}`} className="p-1.5 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"><Check className="w-3 h-3" /></button>
                      <button onClick={() => decide(p.id, "rejected")} data-testid={`reject-${p.id}`} className="p-1.5 border border-red-500/40 text-red-400 hover:bg-red-500/10"><X className="w-3 h-3" /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, eyebrow, color }) => (
  <div className="nexus-card p-4">
    <div className="label-eyebrow">{eyebrow}</div>
    <div className="font-display text-xl mt-1" style={{ color: color || "#F8FAFC" }}>{value}</div>
    <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-white/5">
    <span className="label-eyebrow">{k}</span>
    <span className="font-mono text-sm text-white">{v}</span>
  </div>
);
